import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../api/db';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiTrash2, FiCopy, FiImage, FiLoader, FiArrowLeft } = FiIcons;

const Media = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const loadMedia = async () => {
    setLoading(true);
    try {
      const data = await db.getMedia();
      setMedia(data);
    } catch (e) {
      console.error('Failed to load media', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedia();
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      await db.uploadMedia(file);
      await loadMedia();
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await db.deleteMedia(id);
      await loadMedia();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/admin')}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-2 font-medium mb-6"
        >
          <SafeIcon icon={FiArrowLeft} /> Back to Dashboard
        </button>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Media Library</h1>
            <p className="text-gray-600 mt-1">Upload and manage images for blog articles.</p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-5 py-3 rounded-xl text-white font-medium transition-colors hover:opacity-90 disabled:opacity-70"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <SafeIcon icon={uploading ? FiLoader : FiUpload} className={uploading ? 'animate-spin' : ''} />
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {uploadError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {uploadError}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <p className="text-gray-500">Loading media...</p>
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No images uploaded yet.</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-4 font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Upload your first image
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {media.map(item => (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="aspect-square bg-gray-100 overflow-hidden">
                  <img
                    src={item.thumb_url || item.url}
                    alt={item.title || item.filename || 'Uploaded image'}
                    className="w-full h-full object-cover"
                    onError={e => { e.currentTarget.src = item.url; }}
                  />
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-xs text-gray-500 truncate" title={item.filename || item.title}>
                    {item.filename || item.title || 'Untitled'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyUrl(item.url, item.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--brand-primary)' }}
                    >
                      <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
                      {copiedId === item.id ? 'Copied' : 'Copy URL'}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Media;
